/**
 * Per-agent detail page at /it/agents/:slug.
 *
 * Filters the recent-runs feed to a single agent and shows the spend gauge
 * plus a longer-form description and operator runbook hints.
 *
 * Back link top-left to /it/agents per .claude/rules/cockpit-ui-navigation.md.
 */

import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, RefreshCw } from 'lucide-react';

import {
  formatDuration,
  statusLabel,
  useAgentRuns,
  type AgentRun,
  type AgentSlug,
} from '@/lib/useAgents';
import { relativeTime } from '@/lib/useCockpitOps';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';

function StagePill({ stage }: { stage: AgentRun['agent'] }) {
  const palette: Record<AgentRun['agent'], string> = {
    agent: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    research: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    fix: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    qa: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    unknown: 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted',
  };
  return (
    <Pill tone={palette[stage]} className="capitalize">
      {stage}
    </Pill>
  );
}

function RunStatusPill({ run }: { run: AgentRun }) {
  const { label, tone } = statusLabel(run);
  const tones: Record<typeof tone, string> = {
    ok: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    err: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    idle: 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted',
  };
  return <Pill tone={tones[tone]}>{label}</Pill>;
}

const AGENT_RUNBOOK: Record<AgentSlug, { trigger: string; how_to_drive: string; failure_modes: string }> = {
  code: {
    trigger:
      'Apply the `run-agent` label to any open GitHub issue on AmpersandBrands/cockpit. One agent run takes it end to end: implement → checks → dev deploy + smoke → PR.',
    how_to_drive:
      'A single agent reads the issue, implements the change directly, and runs the repo lint/typecheck/test in a loop until green. It then deploys the branch to dev and smoke-tests it before opening a PR for human review. Add the `agent-opus` label to escalate a hard issue from Sonnet to Opus. Runs are serialized per repo, so multiple `run-agent` labels queue.',
    failure_modes:
      'agent-failed: the agent could not finish (ambiguous issue, or checks still red after its attempts). It leaves a `## What went wrong` comment with a link to the workflow run. Re-label `run-agent` to retry after clarifying.',
  },
  helpdesk: {
    trigger:
      'JSM automation webhook fires on issue-created and customer-commented events to the helpdesk-intake Lambda. (Not yet live — Lambda + webhook still pending build.)',
    how_to_drive:
      'Intake classifies the ticket, drafts a reply, posts a Teams Adaptive Card to the IT reviewer. Approve / Edit / Reject. Decision Lambda posts the JSM comment as Otto and records the human decision. The learn Lambda embeds the decision daily for future retrieval.',
    failure_modes:
      'Same three-tier confidence routing as code-agent (85/60). Risk flags (security, lockout, pii, money) force escalation regardless of confidence. Internal note @-mentions Steven and Cyrus when escalating.',
  },
};

export function AgentDetail() {
  const params = useParams<{ slug: string }>();
  const slug = (params.slug ?? '') as AgentSlug;
  const { agents, recentRuns, isLoading, isFetching, stale, refetch } = useAgentRuns();
  const agent = agents.find((a) => a.slug === slug);
  const runs = recentRuns.filter((r) => {
    // The branch name doesn't encode agent slug, but the workflow name does.
    // Code-agent runs the v2 "Agent" workflow (mapped to 'agent'); the three
    // v1 stage workflows are kept so historical runs still show. Helpdesk-agent
    // will eventually have its own workflow names.
    if (slug === 'code') return ['agent', 'research', 'fix', 'qa'].includes(r.agent);
    return false;
  });

  const cap = agent?.daily_cap_usd ?? null;
  const today = agent?.spend_today_usd ?? null;
  const pct = cap !== null && today !== null && cap > 0 ? Math.min(100, (today / cap) * 100) : 0;

  return (
    <div className="h-full flex flex-col min-h-0">
      <Link
        to="/it/agents"
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to agents
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT - Agents
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            {agent?.name ?? `Unknown agent: ${slug}`}
          </h1>
          {agent ? (
            <p className="mt-1 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted max-w-2xl">
              {agent.description}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {stale ? (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
          GitHub upstream slow or unreachable; showing last cached data.
        </div>
      ) : null}

      {agent ? (
        <div className="rounded-lg border border-amp-line bg-amp-white p-4 mb-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <div className="flex items-baseline justify-between mb-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <span>Today vs daily cap</span>
            <span className="font-medium text-amp-ink dark:text-amp-dark-ink">
              {today !== null ? `$${today.toFixed(2)}` : '—'} /{' '}
              {cap !== null ? `$${cap.toFixed(2)}` : '—'}
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-amp-cream-warm dark:bg-amp-dark-surface-warm overflow-hidden">
            <div
              className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-amp-olive dark:bg-amp-olive-dark'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ) : null}

      {agent && AGENT_RUNBOOK[slug] ? (
        <div className="rounded-lg border border-amp-line bg-amp-white p-4 mb-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <h2 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">
            How this agent works
          </h2>
          <dl className="grid gap-3 text-sm">
            <div>
              <FilterLabel as="div">Trigger</FilterLabel>
              <dd className="text-amp-ink dark:text-amp-dark-ink">
                {AGENT_RUNBOOK[slug].trigger}
              </dd>
            </div>
            <div>
              <FilterLabel as="div">How to drive it</FilterLabel>
              <dd className="text-amp-ink dark:text-amp-dark-ink">
                {AGENT_RUNBOOK[slug].how_to_drive}
              </dd>
            </div>
            <div>
              <FilterLabel as="div">Failure modes</FilterLabel>
              <dd className="text-amp-ink dark:text-amp-dark-ink">
                {AGENT_RUNBOOK[slug].failure_modes}
              </dd>
            </div>
          </dl>
        </div>
      ) : null}

      <DataGridShell
        title="Run history"
        loaded={runs.length}
        countNoun="runs"
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="px-4 py-2 text-left font-medium">Issue</th>
              <th className="px-4 py-2 text-left font-medium">Stage</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-left font-medium">Started</th>
              <th className="px-4 py-2 text-left font-medium">Duration</th>
              <th className="px-4 py-2 text-left font-medium">Run</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {isLoading ? 'Loading runs…' : 'No runs for this agent yet.'}
                </td>
              </tr>
            ) : (
              runs.map((run) => (
                <tr
                  key={run.id}
                  className="border-t border-amp-line dark:border-amp-dark-line"
                >
                  <td className="px-4 py-2">
                    {run.issue_number !== null ? (
                      <a
                        href={`https://github.com/${run.repository}/issues/${run.issue_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={run.issue_title ?? `Issue #${run.issue_number}`}
                        className="text-amp-olive-deep hover:underline dark:text-amp-olive-dark font-medium"
                      >
                        #{run.issue_number}
                      </a>
                    ) : (
                      <span
                        title={run.issue_title ?? run.head_branch ?? ''}
                        className="text-amp-ink-muted dark:text-amp-dark-ink-muted"
                      >
                        {run.issue_title ? run.issue_title.slice(0, 40) + (run.issue_title.length > 40 ? '…' : '') : run.head_branch || '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2"><StagePill stage={run.agent} /></td>
                  <td className="px-4 py-2"><RunStatusPill run={run} /></td>
                  <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
                    {relativeTime(run.started_at)}
                  </td>
                  <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
                    {formatDuration(run.duration_seconds)}
                  </td>
                  <td className="px-4 py-2">
                    <a
                      href={run.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-amp-olive-deep hover:underline dark:text-amp-olive-dark"
                    >
                      Open <ArrowUpRight className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
